import {
    Component,
    Input,
    EventEmitter,
    Output,
    ViewChild,
    AfterViewInit,
    ChangeDetectorRef,
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import intlTelInput, { Iti } from 'intl-tel-input';
import { PhoneNumberValidationError } from './types';
import type { Iso2 } from 'intl-tel-input/data';

@UntilDestroy()
@Component({
    selector: 'app-phone-input',
    templateUrl: './phone-input.component.html',
    styleUrls: ['./phone-input.component.scss'],
})
export class PhoneInputComponent implements AfterViewInit {
    @ViewChild('phoneInput') phoneInput;
    @Input() phoneNumber: string | undefined | null;
    @Input() id: string;
    @Input() isInvalid = false;
    @Input() isRequired = false;
    @Input() isFullWidth = false;
    @Input() customWidth: string | undefined;
    @Input() placeholder = '';
    @Input() disabled = false;

    @Output() phoneNumberChange = new EventEmitter<string>();
    iti: Iti;
    selectedCountryCode: string;
    classWidth: string;

    private _defaultCountry : Iso2 = 'fr';

    @Input()
    set defaultCountry(value) {
        this._defaultCountry = value || 'fr';
        this.tryInitIntlTelInput();
    }

    get defaultCountry() : Iso2 {
        return this._defaultCountry;
    }

    viewInitialized = false;

    constructor(private cdr: ChangeDetectorRef) {}

    ngAfterViewInit(): void {
        this.viewInitialized = true;
        this.phoneInput.nativeElement.value = this.phoneNumber;
        this.initIntlTelInput();
        this.initStyles();
    }

    initIntlTelInput() {
        this.iti = intlTelInput(this.phoneInput.nativeElement, {
            initialCountry: this.defaultCountry,
            countryOrder: ['fr', 'be', 'lu', 'mc', 're'],
            countrySearch: false,
            nationalMode: true,
            showFlags: true,
            formatOnDisplay: false,
            formatAsYouType: false,
            customPlaceholder: () => this.placeholder,
            loadUtils: () => import('intl-tel-input/build/js/utils'),
        });
    }

    initStyles() {
        if (this.customWidth === undefined) {
            this.classWidth = this.isFullWidth ? 'full-width' : 'fixed-width';
            this.cdr.detectChanges();
            return;
        }

        this.phoneInput.nativeElement.style.width = this.customWidth;
    }

    updateAndEmitPhoneNumber() {
        this.selectedCountryCode = this.iti.getSelectedCountryData().dialCode;
        this.phoneNumber = this.iti.getNumber(
            intlTelInput.utils.numberFormat.E164,
        );
        this.phoneNumberChange.emit(this.phoneNumber);
    }

    checkItiValidity(): boolean {
        return !!this.iti.isValidNumber() || !!this.phoneNumber?.trim();
    }

    checkIsInvalidRequired() {
        this.isInvalid = false;

        const error = this.iti.getValidationError();

        this.isInvalid =
            PhoneNumberValidationError[error] !== undefined &&
            this.checkItiValidity();
    }

    checkIsInvalid() {
        this.isInvalid = false;

        const isEmpty = !this.isRequired && !this.phoneNumber;

        const error = this.iti.getValidationError();

        this.isInvalid =
            !isEmpty && PhoneNumberValidationError[error] !== undefined;
    }

    private tryInitIntlTelInput(): void {
        if (this.viewInitialized && this.phoneInput?.nativeElement) {
            this.initIntlTelInput();
        }
    }
}
